<template>
	<app-title-bar
		:icon="['fas', 'fa-truck']"
		subtitle="Hire Desk"
		title="Collection"
		:badges="[collectionNumber, collection?.account_name]">
		<div class="flex gap-3">
			<resource-details :loading="loading || !collection" :resource="collection" />
			<app-button to="/hire-desk/collection-items" icon="fa-circle-left"> Back </app-button>
		</div>
	</app-title-bar>

	<div class="flex flex-col-reverse 2xl:flex-row gap-4">
		<app-panel class="grow" title="Item Details">
			<template #header>
				<div class="flex items-center gap-3">
					<app-badge v-if="collection && collection.multi_journey !== 0"
						>Multi Journeys Exist</app-badge
					>

					<app-badge
						v-for="(weight, depot) in weightByDepot"
						:key="depot"
						theme="info"
						size="small"
						class="gap-1 ml-2 !py-2">
						<font-awesome-icon icon="fa-solid fa-weight-hanging" class="w-4" />
						<b>{{ depot }}:</b> {{ Math.round(weight * 100) / 100 }}
					</app-badge>
				</div>
			</template>

			<app-alert v-if="!loading && items.length == 0" theme="warning">
				There are no items in this collection
			</app-alert>

			<item-details-table
				v-else
				:items="items"
				:loading="loading"
				@updated-items="updated"
				@updated-note="getData" />

			<div class="flex justify-between mt-5 gap-3">
				<div class="flex">
					<app-button
						v-if="collection"
						icon="fa-note-sticky"
						:theme="collection?.notes ? 'danger' : 'primary'"
						:disabled="loading"
						@click="editing = collection">
						{{ collection?.notes ? 'Edit Collection Note' : 'Add Collection Note' }}
					</app-button>
				</div>
				<div v-if="items.length > 0" class="flex gap-3">
					<div class="flex gap-2">
						<depots-dropdown v-model="assignDepot" size="large" />
						<app-button :disabled="!assignDepot" @click="assign"
							>Assign to Depot</app-button
						>
					</div>

					<app-button :disabled="!changed" @click="save">Save</app-button>
				</div>
			</div>
		</app-panel>
	</div>

	<add-edit-collection-note
		:collection="editing"
		@updated="(notes) => (collection!.notes = notes)"
		@closed="editing = null" />
</template>

<script lang="ts" setup>
	import { ref, computed, onMounted } from 'vue'
	import ResourceDetails from '@/components/partials/panels/ResourceDetails.vue'
	import ItemDetailsTable from '@/components/datatables/hire-desk/collections/ItemDetailsTable.vue'
	import type { Collection } from '@/data/schema/collection'
	import type { CollectionItem } from '@/data/schema/collection-item'
	import { useRoute } from 'vue-router'
	import useToaster from '@/stores/toaster'
	import CollectionService from '@/services/collection'
	import { splitWeightByDepot } from '@/utils/collection-items'
	import AddEditCollectionNote from '@/components/partials/cat2/AddEditCollectionNote.vue'
	import DepotsDropdown from '@/components/partials/dropdowns/DepotsDropdown.vue'

	// Resources
	const route = useRoute()
	const api = new CollectionService()
	const toaster = useToaster()

	// Data
	const collectionNumber = ref()
	const loading = ref(true)
	const collection = ref<Collection>()
	const items = ref<Array<CollectionItem>>([])
	const weightByDepot = ref()
	const editing = ref<Collection | null>(null)
	const assignDepot = ref<string | null>(null)

	/**
	 * Get contract and contract item data
	 * @param init boolean - exclusive for onMounted
	 */
	async function getData() {
		loading.value = true

		api.get(collectionNumber.value, { withItems: true })
			.then((res) => {
				collection.value = res.data.data

				if (collection.value && collection.value.items) {
					items.value = collection.value?.items.map((item) => ({
						...item,
						changed: false
					}))

					items.value.forEach((item) => {
						if (item.kit_components) {
							item.kit_components = item.kit_components.map((kit) => ({
								...kit,
								changed: false
							}))
						}
					})
				}

				if (collection.value?.depot) {
					assignDepot.value = collection.value.depot
				}

				weightByDepot.value = splitWeightByDepot(items.value)

				loading.value = false
			})
			.catch((error) => {
				console.error(error)
			})
	}

	/**
	 * Items have been updated
	 */
	function updated(item: CollectionItem, kit: CollectionItem) {
		const index = items.value.findIndex((i) => i.id === item.id)

		if (index !== -1 && !kit) {
			items.value[index].complete_flag = item.complete_flag
			items.value[index].changed = !items.value[index].changed
		}

		if (index !== -1 && kit) {
			const kitIndex = items.value[index].kit_components!.findIndex((i) => i.id === kit.id)

			// Kit Complete Flag
			items.value[index].kit_components![kitIndex].complete_flag = kit.complete_flag

			// Kit Changed
			items.value[index].kit_components![kitIndex].changed =
				!items.value[index]?.kit_components?.[kitIndex].changed
		}
	}

	const changed = computed(() => {
		return items.value
			.flatMap((item) => [item, ...(item.kit_components || [])])
			.some((item) => item.changed)
	})

	function save() {
		const changedItems = items.value
			.flatMap((item) => [item, ...(item.kit_components || [])])
			.filter((item) => item.changed === true)

		const saveItems = changedItems.map((item) => ({
			id: item.id,
			complete_flag: item.complete_flag
		}))

		toaster.working({
			text: 'Saving contract item changes...',
			dismissable: false,
			timeout: 0
		})

		api.updateItems(saveItems)
			.then(() => {
				toaster.clear()
				toaster.success({
					text: 'Changes saved successfully'
				})
				getData()
			})
			.catch((error) => {
				console.error(error)
			})
	}

	function assign() {
		toaster.working({
			text: 'Assigning collection to depot...',
			dismissable: false,
			timeout: 0
		})

		api.updateCollection(collectionNumber.value, {
			depot: assignDepot.value
		})
			.then(() => {
				toaster.clear()
				toaster.success({
					text: 'Depot updated successfully'
				})
			})
			.catch((error) => {
				console.error(error)
			})
	}

	onMounted(() => {
		collectionNumber.value = route.params.id

		if (collectionNumber.value) {
			getData()
		}
	})
</script>
